/**
 * TeamTableBody – renders a single <TableRow> for one team record.
 *
 * Used by TeamManagement via CommonDataTable's `renderRow` prop.
 * The action menu is managed per-row internally via a local anchor state.
 */
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    TableCell, TableRow, IconButton, Chip, Tooltip, Box, Menu, Link, Typography
} from '@mui/material';
import {
    Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon,
    Groups as GroupsIcon
} from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';

import * as actions from '../redux.actions';
import StatusChip from './StatusChip';

const StyledMenuBox = styled(Box)(({ theme }) => ({
    display: 'flex', flexDirection: 'column', padding: theme.spacing(2), gap: theme.spacing(2)
}));

const menuLinkStyles = {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%', height: '32px',
    padding: '8px 12px', border: '1px solid', borderColor: 'primary.main', borderRadius: '4px',
    color: 'primary.main', textDecoration: 'none', fontSize: '13px', fontWeight: 400, cursor: 'pointer',
    pl: 3, transition: 'all 0.2s ease-in-out',
    '&:hover': { backgroundColor: 'rgba(225, 67, 0, 0.1)', borderColor: 'rgb(225, 67, 0)', color: 'rgb(225, 67, 0)' }
};
const deleteLinkStyles = {
    ...menuLinkStyles, borderColor: 'error.main', color: 'error.main',
    '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)', borderColor: 'error.dark', color: 'error.dark' }
};

const formatDate = (d) => (d ? dayjs(d).format('MMM DD, YYYY') : '-');

const TeamTableBody = ({ team: t }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [anchor, setAnchor] = React.useState(null);

    const handleEdit = () => dispatch(actions.openTeamDialog(t));
    const handleDelete = () => dispatch(actions.openDeleteDialog(t));
    const handleView = () => navigate(`/teams/${t.id}`);

    const handleRowClick = (e) => {
        if (e.target.closest('button, a, input, [role="button"]')) return;
        if (e.metaKey || e.ctrlKey) {
            window.open(`/teams/${t.id}`, '_blank');
        } else {
            navigate(`/teams/${t.id}`);
        }
    };

    const handleAuxClick = (e) => {
        if (e.button === 1) { // Middle click
            if (e.target.closest('button, a, input, [role="button"]')) return;
            window.open(`/teams/${t.id}`, '_blank');
        }
    };

    const openMenu = (e) => { e.stopPropagation(); setAnchor(e.currentTarget); };
    const closeMenu = () => setAnchor(null);

    return (
        <>
            <TableRow
                hover
                onClick={handleRowClick}
                onAuxClick={handleAuxClick}
                sx={{ '&:last-child td': { border: 0 }, cursor: 'pointer' }}
            >
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupsIcon sx={{ color: (theme) => theme.palette.text.secondary, fontSize: 20 }} />
                        <Link
                            component={RouterLink}
                            to={`/teams/${t.id}`}
                            color='inherit'
                            underline='hover'
                            sx={{ fontWeight: 'bold' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {t.name}
                        </Link>
                    </Box>
                </TableCell>
                <TableCell>
                    <Tooltip title={t.description || ''}>
                        <span style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {t.description || '-'}
                        </span>
                    </Tooltip>
                </TableCell>
                <TableCell align='center'>
                    <Chip
                        label={Array.isArray(t.members) ? t.members.length : (t.memberCount ?? 0)}
                        size='small'
                        variant='outlined'
                    />
                </TableCell>
                <TableCell align='center'>
                    <StatusChip active={!!t.isActive} />
                </TableCell>
                <TableCell>{formatDate(t.updatedAt || t.createdAt)}</TableCell>
                <TableCell align='center'>
                    <Tooltip title='More'>
                        <IconButton size='small' onClick={openMenu}>
                            <MoreVertIcon />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>

            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <StyledMenuBox>
                    <Link component={RouterLink} to={`/teams/${t.id}`} onClick={closeMenu} sx={menuLinkStyles}>
                        <VisibilityIcon sx={{ mr: 1, fontSize: '18px' }} /> View Details
                    </Link>
                    <Link component='button' onClick={(e) => { e.preventDefault(); handleEdit(); closeMenu(); }} sx={menuLinkStyles}>
                        <EditIcon sx={{ mr: 1, fontSize: '18px' }} /> Edit
                    </Link>
                    <Link component='button' onClick={(e) => { e.preventDefault(); handleDelete(); closeMenu(); }} sx={deleteLinkStyles}>
                        <DeleteIcon sx={{ mr: 1, fontSize: '18px' }} /> Delete
                    </Link>
                </StyledMenuBox>
            </Menu>
        </>
    );
};

export default React.memo(TeamTableBody);
